import { Button, Grid, Paper } from "@material-ui/core";
import PromptUnsavedChanges from "components/PromptUnsavedChanges/PromptUnsavedChanges";
import { useGetAllActivityItemsQuery, useGetAllActivitiesQuery, useGetWorkOrderInfoQuery } from "features/activity/activitySlice";
import AttachmentsView from "features/attachments/AttachmentsView";
import { AttachmentEntityTypes } from "features/attachments/attachmentsConsts";
import { FlagEntityTypes } from "features/flag/flagsConsts";
import { FlagAsset } from "features/vehicles/Flags/FlagAsset";
import IconMechanicalWrapper from "icons/mechanicals/IconMechanicalWrapper";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import LoadingSpinner from "../../components/LoadingSpinner";
import { IconBox, IconForm, IconText } from "../../icons";
import NoteList from "../notes/NoteList";
import { NoteEntityTypes } from "../notes/notesConsts";
import CardHeader from "./cardHeader";
import InventoryUsed from "./components/Inventory/InventoryUsed";
import MechanicalWorkDetails from "./components/MechanicalWorkDetails/MechanicalWorkDetails";
import StepsToComplete from "./components/StepsToComplete/StepsToComplete";
import { mechanicalWorkTaskStyle } from "./mechanicalWorkTaskStyle";
import Authorize from "components/Authorize";
import { permissionProfiles } from "components/Authorize/permissionProfiles";
import { formTypes } from "features/Forms/formBuilder/formBuilderConsts";
import { arrayToObject } from "utils/object-util";
import { useGetFormsForClientQuery } from "features/Forms/formBuilder/formBuilderSlice";
import { clientConfigTypes, clientConfigTypeToName } from "features/clients/clientConfigsSetup";
import { useGetClientsWithConfigsQuery } from "features/clients/clientConfigSlice";
import ActivityCustomForm from "features/vehicles/VehicleDetailsPage/ActivityCustomForm/ActivityCustomForm";
import PricingDetails from "./components/PricingDetails/PricingDetails";
import HotNotePanel from "./components/HotNotePanel/HotNotePanel";

const ActivityWorkOrder = () => {
    const params = useParams();

    const classes = mechanicalWorkTaskStyle();

    const [existingImages, setExistingImages] = useState();
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

    let { data: activityResult, error: vehicleDetailsError, isLoading: isVehicleDetailsLoading, refetch: refecthLog } = useGetWorkOrderInfoQuery(params.id);
    let { data: activityItems, error: activityItemError, isLoading: isLoadingActivityItems } = useGetAllActivityItemsQuery();
    let { data: allActivities } = useGetAllActivitiesQuery();
    let { data: forms, error: formError, isFetching: isLoadingForms } = useGetFormsForClientQuery({ formType: formTypes.Activity, clientId: activityResult?.data?.vehicle?.clientId }, { skip: !activityResult?.data?.vehicle?.clientId });
    let { data: clientsWithConfigs, error: clientsWithConfigsError, isLoading: isLoadingClientsWithConfigs, isFetching: isFetchingClientsWithConfigs } = useGetClientsWithConfigsQuery(activityResult?.data?.vehicle?.clientId, { skip: !activityResult?.data?.vehicle?.clientId });
    forms = forms || [];

    let currentActivityItem = activityItems?.find(a => a.id === activityResult?.data?.activityItemId);
    const activityLookup = useMemo(() => arrayToObject(allActivities), [allActivities]);
    const currentStatus = activityLookup?.[activityResult?.data?.activityId];

    const formsLookup = arrayToObject(forms, 'activityItemId');
    const clientConfig = clientsWithConfigs?.[0]?.config?.[clientConfigTypeToName[clientConfigTypes.Mechanicals]];
    const useCustomActivityConfig = clientConfig?.['useCustomActivity'] ?? [];
    const useCustomActivityConfigLookup = arrayToObject(useCustomActivityConfig, 'activityItemId');

    const [customFormOpen, setCustomFormOpen] = useState(false);

    useEffect(() => {
        if (!existingImages) {
            setExistingImages(activityResult?.data?.images);
        }

    }, [activityResult?.data])

    if (isVehicleDetailsLoading) {
        return <LoadingSpinner loading={isVehicleDetailsLoading} />
    }

    return (
        <>
            <PromptUnsavedChanges
                hasUnsavedChanges={hasUnsavedChanges}
            />

            <div className={classes.pageTitleWrapper}>
                <div className={classes.pageTitleRow}>
                    <IconMechanicalWrapper activityItem={currentActivityItem} className={classes.typeIcon} />
                    <h1 className={classes.pageTitle}>{`${currentActivityItem?.name} Work Order`}</h1>
                    {currentStatus && <span className={classes.statusBadge}>{currentStatus.name}</span>}
                    <FlagAsset
                        selectedRows={[activityResult?.data?.id]}
                        allData={[{ ...activityResult?.data, vehicleId: activityResult?.data?.vehicle?.id }]}
                        entityType={FlagEntityTypes.ACTIVITY}
                        iconOnly
                    />
                    <HotNotePanel activityId={activityResult?.data?.id} />
                </div>
            </div>

            <Paper className={`${classes.card}`} elevation={1}>
                <div className={classes.header}>
                    <div className={classes.sectionTitle}>Details</div>
                    {formsLookup?.[currentActivityItem?.id] && useCustomActivityConfigLookup?.[currentActivityItem?.id]?.on &&
                        <Button
                            startIcon={<IconForm width="16" />}
                            onClick={() => {
                                setCustomFormOpen(activityResult?.data);
                            }}
                            variant="outlined"
                            className={classes.formBtn}
                        >
                            View Form
                        </Button>
                    }
                </div>

                <MechanicalWorkDetails activity={activityResult?.data} currentActivityItem={currentActivityItem} />
            </Paper>

            {/*Steps to complete*/}
            <Paper className={`${classes.card}`} elevation={1}>
                <CardHeader icon={<IconText />} title='Steps to complete:' />
                <Grid container direction="row" alignItems="center" justifyContent="space-between" spacing={2}
                    className={classes.details}>
                    <StepsToComplete activity={activityResult?.data} setHasUnsavedChanges={setHasUnsavedChanges} />
                </Grid>
            </Paper>

            {/*Inventory used*/}
            <Paper className={`${classes.card}`} elevation={1}>
                <CardHeader title='Inventory used' icon={<IconBox />} />
                <Grid container direction="row" alignItems="center" justifyContent="space-between" spacing={2}
                    className={classes.details}>
                    <InventoryUsed activity={activityResult?.data} setHasUnsavedChanges={setHasUnsavedChanges} />
                </Grid>
            </Paper>

            {/*Notes*/}
            <Authorize profile={permissionProfiles.NOTES.VIEW_NOTES}>
                <Paper className={`${classes.card}`} elevation={1}>
                    <NoteList entityId={activityResult?.data?.id} entityType={NoteEntityTypes.ACTIVITY} />
                </Paper>
            </Authorize>

            {/*Photos & Documents*/}
            <Paper className={`${classes.card}`} elevation={1}>
                <div className={classes.sideBySide}>
                    <AttachmentsView entityId={activityResult?.data?.id} entityType={AttachmentEntityTypes.ACTIVITY} showTitle={true} noteType={NoteEntityTypes.WORKORDER_ATTACHMENT} />
                </div>
            </Paper>
            
            {/*Pricing*/}
            <Paper className={`${classes.card}`} elevation={1}>
                <PricingDetails activity={activityResult?.data}/>
            </Paper>

            <ActivityCustomForm
                vehicleDetails={activityResult?.data?.vehicle}
                activityLog={customFormOpen}
                onCancel={() => {
                    setCustomFormOpen(null);
                }} />
        </>
    )
}

export default ActivityWorkOrder;


